import * as React from 'react';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../theme';
import Navigation from './components/Navigation';
import { NavigationSlim } from './components/NavigationSlim';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contextProviders/AuthContext';
import { RoleType } from '../models/User';
import strings from '../strings/strings.json';
import NavigationPublic, { NavigationSignOutOnly } from './components/NavigationPublic';
import { BuildCourseProvider } from 'contextProviders/BuildCourseContext';
import Footer, { ProfileFooter } from '../components/footer/Footer';
import { LearnerCourseProvider, LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { FlexColAlignCenter } from 'components/Elements';

export function HomepageLayout(): JSX.Element {
    return <AppMainLayout navType={'none'} footerType={'standard'} />;
}

export function PublicLayout(): JSX.Element {
    const { isAuthenticated, userData } = React.useContext(AuthContext);

    if (isAuthenticated && !userData.roles.length) {
        return <Navigate to="/initial-login" />;
    }

    return <AppMainLayout navType={isAuthenticated ? 'standard' : 'public'} footerType={'standard'} />;
}

export function InitialLoginLayout(): JSX.Element {
    const { userData, isAuthenticated } = React.useContext(AuthContext);
    if (isAuthenticated && userData.roles.length) {
        return <Navigate to="/" />;
    }
    return <AppMainLayout navType={'signOutOnly'} footerType={'standard'} />;
}

export function AdminLayout(): JSX.Element {
    const { userData, setActiveRole } = React.useContext(AuthContext);

    if (!userData.roles.includes(RoleType.ADMIN)) {
        return <Navigate to="/" />;
    }

    if (userData.activeRole !== RoleType.ADMIN) setActiveRole(RoleType.ADMIN);

    return <AppMainLayout navType={'standard'} footerType={'standard'} />;
}

export function OrganisationLayout(): JSX.Element {
    const { userData, setActiveRole } = React.useContext(AuthContext);

    if (!userData.roles.includes(RoleType.ORGANISATION_ADMIN)) {
        return <Navigate to="/" />;
    }

    if (userData.activeRole !== RoleType.ORGANISATION_ADMIN) setActiveRole(RoleType.ORGANISATION_ADMIN);

    return <AppMainLayout navType={'standard'} footerType={'standard'} />;
}

export function OrganisationOrTrainerLayout(): JSX.Element {
    const { userData } = React.useContext(AuthContext);

    if (
        !userData.roles.some((role) => {
            return role === RoleType.TRAINER || role === RoleType.ORGANISATION_ADMIN;
        })
    ) {
        return <Navigate to="/" />;
    }

    return <AppMainLayout navType={'standard'} footerType={'standard'} />;
}

export function LearnerLayout(): JSX.Element {
    const { userData, setActiveRole } = React.useContext(AuthContext);

    if (!userData.roles.includes(RoleType.LEARNER)) {
        return <Navigate to="/" />;
    }

    if (userData.activeRole !== RoleType.LEARNER) setActiveRole(RoleType.LEARNER);

    return <AppMainLayout navType={'standard'} footerType={'standard'} />;
}

export function LearnerCourseLayout(): JSX.Element {
    const { userData, setActiveRole } = React.useContext(AuthContext);

    if (!userData.roles.includes(RoleType.LEARNER)) {
        return <Navigate to="/" />;
    }

    if (userData.activeRole !== RoleType.LEARNER) setActiveRole(RoleType.LEARNER);

    return (
        <LearnerCourseProvider previewMode={false}>
            <AppMainLayout navType={'standard'} footerType={'standard'} />
        </LearnerCourseProvider>
    );
}

export function MainLayout(): JSX.Element {
    const { userData } = React.useContext(AuthContext);

    if (!userData.roles.length) return <Navigate to="/" />;

    return <AppMainLayout navType={'standard'} footerType={'standard'} />;
}

export function ProfileLayout(): JSX.Element {
    const { userData } = React.useContext(AuthContext);

    if (!userData.roles.length) return <Navigate to="/" />;

    return <AppMainLayout navType={'standard'} footerType={'profile'} />;
}

type State = {
    previewContextEntryPoint: string;
};

export function PreviewLayout(): JSX.Element {
    const { userData } = React.useContext(AuthContext);
    if (
        !userData.roles.some((role) => {
            return role === RoleType.TRAINER || role === RoleType.ORGANISATION_ADMIN;
        })
    ) {
        return <Navigate to="/" />;
    }

    return (
        <LearnerCourseProvider previewMode>
            <AppMainLayout navType={'slim'} footerType={'standard'} />
        </LearnerCourseProvider>
    );
}

type CourseBuilderLayoutProps = {
    editMode: boolean;
};

export function CourseBuilderLayout({ editMode }: CourseBuilderLayoutProps): JSX.Element {
    const { userData } = React.useContext(AuthContext);

    if (
        !userData.roles.some((role) => {
            return role === RoleType.TRAINER || role === RoleType.ORGANISATION_ADMIN;
        })
    ) {
        return <Navigate to="/" />;
    }

    return (
        <BuildCourseProvider editMode={editMode}>
            <AppMainLayout navType={'standard'} footerType={'none'} alignContentLeft />
        </BuildCourseProvider>
    );
}

type AppMainLayoutProps = {
    navType: NavType;
    footerType: FooterType;
    alignContentLeft?: boolean;
};

type NavType = 'none' | 'standard' | 'public' | 'signOutOnly' | 'slim';
type FooterType = 'none' | 'standard' | 'profile';

function AppMainLayout({
    alignContentLeft,
    navType = 'standard',
    footerType = 'standard',
}: AppMainLayoutProps): JSX.Element {
    const learnerCourseContext = React.useContext(LearnerCourseContext);
    const navigate = useNavigate();
    const { state } = useLocation();
    const [previewEntryPoint] = React.useState((state as State)?.previewContextEntryPoint);

    const immersiveMode = learnerCourseContext.immersiveMode;
    if (immersiveMode) {
        navType = 'slim';
    }

    let navComponent = <></>;
    let marginTop = '';
    let marginTopMobile = '';

    switch (navType) {
        case 'none':
            navComponent = <></>;
            marginTop = '0px';
            marginTopMobile = '0px';
            break;
        case 'signOutOnly':
            navComponent = <NavigationSignOutOnly />;
            marginTop = sizes.headerHeight;
            marginTopMobile = sizes.headerMobileHeight;
            break;
        case 'public':
            navComponent = <NavigationPublic />;
            marginTop = sizes.headerHeight;
            marginTopMobile = sizes.headerMobileHeight;
            break;
        case 'slim':
            const closePreview = () => {
                navigate(previewEntryPoint ?? '/');
            };
            navComponent = (
                <NavigationSlim
                    buttonTitle={
                        immersiveMode ? strings.navigation.backToCourseTitle : strings.navigation.closePreviewTitle
                    }
                    buttonAria={
                        immersiveMode ? strings.navigation.backToCourseAria : strings.navigation.closePreviewAria
                    }
                    buttonOnClick={
                        immersiveMode
                            ? () => learnerCourseContext.setStep({ id: '', stepType: 'Course' })
                            : closePreview
                    }
                />
            );
            marginTop = sizes.headerHeight;
            marginTopMobile = sizes.headerMobileHeightSlim;
            break;
        case 'standard':
        default:
            navComponent = <Navigation />;
            marginTop = sizes.headerHeight;
            marginTopMobile = sizes.headerMobileHeight;
            break;
    }

    let footerComponent = <></>;

    switch (footerType) {
        case 'none':
            footerComponent = <></>;
            break;
        case 'profile':
            footerComponent = <ProfileFooter />;
            break;
        case 'standard':
        default:
            footerComponent = <Footer />;
            break;
    }

    return (
        <App>
            <SkipToContentLink href="#content" aria-label={strings.skipToContentLink.aria}>
                {strings.skipToContentLink.title}
            </SkipToContentLink>
            {navComponent}
            <MainContainer
                id="content"
                $alignLeft={!!alignContentLeft}
                $marginTop={marginTop}
                $marginTopMobile={marginTopMobile}
            >
                <Outlet />
            </MainContainer>
            {footerComponent}
        </App>
    );
}

const MainContainer = styled.main<{
    $alignLeft: boolean;
    $marginTop: string;
    $marginTopMobile: string;
}>`
    display: flex;
    flex-direction: column;
    align-items: ${({ $alignLeft }) => ($alignLeft ? 'start' : 'center')};
    height: 100%;
    width: 100%;
    z-index: 0;
    flex: 1;

    margin-top: ${({ $marginTop }) => $marginTop};
    @media (max-width: ${breakpoints.sm}) {
        margin-top: ${({ $marginTopMobile }) => $marginTopMobile};
    }
`;

const App = styled(FlexColAlignCenter)`
    width: 100%;
    background-color: ${theme.appBackgroundColour};
    min-height: 100vh;
`;

const SkipToContentLink = styled.a`
    position: absolute;
    top: -400px;
    &:focus {
        top: 0;
        left: 10px;
        z-index: 121; //overlay navigation
    }
`;
